import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { alpha } from '@mui/material/styles';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Grid, Stack, TextField, Typography,
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PublicIcon from '@mui/icons-material/Public';
import PageContainer from '../components/PageContainer';
import AppModuleScaffold from '../components/AppModuleScaffold';
import { getActiveFees } from '../api/services';
import type { ActiveFees, Fee } from '../../types/domain';
import { FEE_AGREEMENT_LABEL } from '../brand';
import { useIsXs } from '../hooks/useMobileLayout';

type LocationState = { sindicatoId?: number | null; feeCode?: 'AGREEMENT' | 'NO_AGREEMENT' | null };

const cardRadius = { xs: '14px', sm: '12px' };

export default function Home() {
  const isXs = useIsXs();
  const navigate = useNavigate();
  const location = useLocation() as { state?: LocationState };
  const suggested = location.state?.feeCode;

  const [data, setData] = useState<ActiveFees | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<'AGREEMENT' | 'NO_AGREEMENT' | null>(null);
  const [doc, setDoc] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await getActiveFees({
          sindicatoId: undefined,
        });
        if (cancelled) return;
        const next: ActiveFees = { agreement: result.agreement, noAgreement: result.noAgreement };
        setData(next);
        if (suggested === 'AGREEMENT' && next.agreement) setSelected('AGREEMENT');
        else if (suggested === 'NO_AGREEMENT' && next.noAgreement) setSelected('NO_AGREEMENT');
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          || 'No se pudieron obtener las tarifas';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [suggested]);

  const fee: Fee | null | undefined = selected === 'AGREEMENT' ? data?.agreement : selected === 'NO_AGREEMENT' ? data?.noAgreement : undefined;

  const options = useMemo(() => ([
    {
      code: 'AGREEMENT' as const,
      feeOpt: data?.agreement,
      title: 'DNI',
      subtitle: `${FEE_AGREEMENT_LABEL} — cobro con documento nacional`,
      icon: <HandshakeIcon sx={{ fontSize: 36, color: 'success.main' }} />,
      border: 'success.main',
    },
    {
      code: 'NO_AGREEMENT' as const,
      feeOpt: data?.noAgreement,
      title: 'Pasaporte',
      subtitle: 'Sin acuerdo — cobro con documento internacional',
      icon: <PublicIcon sx={{ fontSize: 36, color: 'info.main' }} />,
      border: 'info.main',
    },
  ]), [data?.agreement, data?.noAgreement]);

  const docLabel = useMemo(() => (fee?.documentType === 'PASSPORT' ? 'Pasaporte' : 'DNI'), [fee?.documentType]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) {
      setError('Elegí si el pago es con DNI o con pasaporte.');
      return;
    }
    if (!fee) {
      setError('No hay tarifa activa para esa opción.');
      return;
    }
    const trimmed = doc.trim();
    if (!trimmed) {
      setError(`Ingresá el ${docLabel.toLowerCase()} del titular.`);
      return;
    }
    setError(null);
    navigate(`/checkout/${selected}`, {
      state: { sindicatoId: null, documentNumber: trimmed },
      replace: false,
    });
  }

  if (loading) {
    return (
      <PageContainer maxWidth="md" sx={{ pt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={36} color="primary" />
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="md" sx={{ pb: { xs: 10, md: 4 }, pt: { xs: 0, sm: 1 } }}>
      <AppModuleScaffold title="¿ingresa el documento con el que vas abonar ?">

        {error && <Alert severity="error" variant="outlined" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          DNI ({FEE_AGREEMENT_LABEL}) o pasaporte (sin acuerdo). El importe y el pago (Mercado Pago simulado) son al final.
        </Typography>

        <Grid container spacing={2} data-tour="tour-home-fees">
          {options.map((opt) => {
            const active = selected === opt.code;
            const disabled = !opt.feeOpt;
            return (
              <Grid key={opt.code} size={{ xs: 12, sm: 6 }}>
                <Card
                  elevation={0}
                  variant="outlined"
                  sx={{
                    borderRadius: cardRadius,
                    borderWidth: 2,
                    borderColor: active ? opt.border : 'divider',
                    bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.04) : 'background.paper',
                    opacity: disabled ? 0.55 : 1,
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      if (!disabled) {
                        setSelected(opt.code);
                        setDoc('');
                        setError(null);
                      }
                    }}
                    disabled={disabled}
                    sx={{ alignItems: 'stretch', height: '100%' }}
                  >
                    <CardContent sx={{ p: 2.25 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box>
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                            {opt.code === 'AGREEMENT' ? 'Documento nacional' : 'Documento internacional'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>{opt.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{opt.subtitle}</Typography>
                          {!opt.feeOpt && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Sin tarifa configurada para esta opción.
                            </Typography>
                          )}
                        </Box>
                        {opt.icon}
                      </Stack>
                      {active && (
                        <Chip size="small" color="primary" label="Seleccionado" sx={{ mt: 2, fontWeight: 700 }} />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {selected && fee && (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Stack spacing={0.5} sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">Tipo de cobro</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {selected === 'AGREEMENT' ? FEE_AGREEMENT_LABEL : 'Sin Acuerdo'}
              </Typography>
              <Typography variant="body2" color="text.secondary">{fee.name}</Typography>
            </Stack>
            <TextField
              label={docLabel}
              value={doc}
              onChange={(e) => setDoc(e.target.value)}
              required
              autoFocus
              fullWidth
              size="small"
              inputMode={fee.documentType === 'PASSPORT' ? 'text' : 'numeric'}
              helperText={fee.documentType === 'PASSPORT' ? 'Pasaporte alfanumérico' : 'Solo números, sin puntos'}
              sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" size="large" fullWidth={isXs} sx={{ fontWeight: 800, py: 1.25, borderRadius: 999, textTransform: 'none' }}>
              Ver importe y pagar
            </Button>
          </Box>
        )}
      </AppModuleScaffold>
    </PageContainer>
  );
}
